import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePackingSlip,
  usePreparePackingSlip,
  useVerifyPackingSlip,
  useShipPackingSlip,
  useConfirmDelivery,
  useCancelPackingSlip,
} from '../hooks/useWmsShipping';
import { PackingSlipStatusBadge } from '../components/shipping/PackingSlipStatusBadge';
import { PackingSlipItemsTable } from '../components/shipping/PackingSlipItemsTable';
import { ShipPackingSlipModal } from '../components/shipping/ShipPackingSlipModal';
import type { PackingSlip, ShipPackingSlipInput } from '../types';
import { CARRIERS } from '../types';

export default function PackingSlipDetailPage() {
  const { id } = useParams<{ id: string }>();

  // Modal state
  const [showShipModal, setShowShipModal] = useState(false);

  // Queries
  const packingSlipQuery = usePackingSlip(id || '');

  // Mutations
  const prepareMutation = usePreparePackingSlip();
  const verifyMutation = useVerifyPackingSlip();
  const shipMutation = useShipPackingSlip();
  const deliverMutation = useConfirmDelivery();
  const cancelMutation = useCancelPackingSlip();

  const packingSlip = packingSlipQuery.data as PackingSlip | undefined;
  const items = packingSlip?.items || [];

  const handlePrepare = async () => {
    if (!id) return;
    await prepareMutation.mutateAsync(id);
  };

  const handleVerify = async () => {
    if (!id) return;
    await verifyMutation.mutateAsync(id);
  };

  const handleShip = async (data: ShipPackingSlipInput) => {
    if (!id) return;
    await shipMutation.mutateAsync({ id, data });
  };

  const handleDelivered = async () => {
    if (!id) return;
    await deliverMutation.mutateAsync(id);
  };

  const handleCancel = async () => {
    if (!id) return;
    const reason = prompt('Cancellation reason (optional):');
    await cancelMutation.mutateAsync({ id, reason: reason || undefined });
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const getCarrierLabel = (value?: string) => {
    if (!value) return '—';
    const carrier = CARRIERS.find((c) => c.value === value);
    return carrier?.label || value;
  };

  if (packingSlipQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (packingSlipQuery.error || !packingSlip) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-destructive">Packing Slip not found</h2>
        <p className="text-muted-foreground mt-2">
          The packing slip you're looking for doesn't exist.
        </p>
        <Button asChild className="mt-4">
          <Link to="/wms/shipping">Back to Shipping</Link>
        </Button>
      </div>
    );
  }

  const canPrepare = packingSlip.status === 'draft';
  const canVerify = packingSlip.status === 'packing';
  const canShip = packingSlip.status === 'packed';
  const canDeliver = packingSlip.status === 'shipped';
  const canCancel = !['shipped', 'delivered', 'cancelled'].includes(packingSlip.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/wms/shipping"
              className="text-muted-foreground hover:text-foreground"
            >
              Shipping
            </Link>
            <span className="text-muted-foreground">/</span>
            <h1 className="text-2xl font-bold tracking-tight font-mono">
              {packingSlip.slip_number}
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Work Order: {packingSlip.work_order_number || '—'}
            {packingSlip.account_name && ` • ${packingSlip.account_name}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {canPrepare && (
            <Button onClick={handlePrepare} disabled={prepareMutation.isPending}>
              {prepareMutation.isPending ? 'Preparing...' : 'Start Packing'}
            </Button>
          )}
          {canVerify && (
            <Button onClick={handleVerify} disabled={verifyMutation.isPending}>
              {verifyMutation.isPending ? 'Verifying...' : 'Verify & Pack'}
            </Button>
          )}
          {canShip && (
            <Button onClick={() => setShowShipModal(true)}>
              Ship
            </Button>
          )}
          {canDeliver && (
            <Button onClick={handleDelivered} disabled={deliverMutation.isPending}>
              {deliverMutation.isPending ? 'Confirming...' : 'Confirm Delivery'}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              className="text-red-600"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PackingSlipStatusBadge status={packingSlip.status} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Carrier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{getCarrierLabel(packingSlip.carrier)}</p>
            {packingSlip.service_type && (
              <p className="text-sm text-muted-foreground">{packingSlip.service_type}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Tracking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono font-medium">
              {packingSlip.tracking_number || '—'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Packages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{packingSlip.package_count}</p>
            {packingSlip.weight_kg && (
              <p className="text-sm text-muted-foreground">
                {packingSlip.weight_kg.toFixed(2)} kg
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Shipping Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Ship To</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {packingSlip.ship_to_address ? (
              <>
                {packingSlip.ship_to_address.name && (
                  <p className="font-medium">{packingSlip.ship_to_address.name}</p>
                )}
                {packingSlip.ship_to_address.address_line_1 && (
                  <p>{packingSlip.ship_to_address.address_line_1}</p>
                )}
                {packingSlip.ship_to_address.address_line_2 && (
                  <p>{packingSlip.ship_to_address.address_line_2}</p>
                )}
                <p>
                  {[
                    packingSlip.ship_to_address.city,
                    packingSlip.ship_to_address.state,
                    packingSlip.ship_to_address.postal_code,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                {packingSlip.ship_to_address.country && (
                  <p>{packingSlip.ship_to_address.country}</p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">No address specified</p>
            )}
            {packingSlip.ship_to_contact && (
              <div className="pt-2 border-t mt-2">
                {packingSlip.ship_to_contact.name && (
                  <p>Contact: {packingSlip.ship_to_contact.name}</p>
                )}
                {packingSlip.ship_to_contact.phone && (
                  <p>Phone: {packingSlip.ship_to_contact.phone}</p>
                )}
                {packingSlip.ship_to_contact.email && (
                  <p>Email: {packingSlip.ship_to_contact.email}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Timeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span>{formatDateTime(packingSlip.created_at)}</span>
            </div>
            {packingSlip.prepared_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Prepared:</span>
                <span>
                  {formatDateTime(packingSlip.prepared_at)}
                  {packingSlip.prepared_by_name && ` by ${packingSlip.prepared_by_name}`}
                </span>
              </div>
            )}
            {packingSlip.verified_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Verified:</span>
                <span>
                  {formatDateTime(packingSlip.verified_at)}
                  {packingSlip.verified_by_name && ` by ${packingSlip.verified_by_name}`}
                </span>
              </div>
            )}
            {packingSlip.shipped_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipped:</span>
                <span>
                  {formatDateTime(packingSlip.shipped_at)}
                  {packingSlip.shipped_by_name && ` by ${packingSlip.shipped_by_name}`}
                </span>
              </div>
            )}
            {packingSlip.delivered_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivered:</span>
                <span>{formatDateTime(packingSlip.delivered_at)}</span>
              </div>
            )}
            {packingSlip.shipping_cost && (
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Shipping Cost:</span>
                <span className="font-medium">${packingSlip.shipping_cost.toFixed(2)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {(packingSlip.special_instructions || packingSlip.internal_notes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {packingSlip.special_instructions && (
            <Card>
              <CardHeader>
                <CardTitle>Special Instructions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{packingSlip.special_instructions}</p>
              </CardContent>
            </Card>
          )}
          {packingSlip.internal_notes && (
            <Card>
              <CardHeader>
                <CardTitle>Internal Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{packingSlip.internal_notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Items */}
      <Card>
        <CardHeader>
          <CardTitle>Packed Items</CardTitle>
        </CardHeader>
        <CardContent>
          <PackingSlipItemsTable items={items} />
        </CardContent>
      </Card>

      {/* Ship Modal */}
      <ShipPackingSlipModal
        open={showShipModal}
        onClose={() => setShowShipModal(false)}
        onSubmit={handleShip}
        packingSlip={packingSlip}
        isLoading={shipMutation.isPending}
      />
    </div>
  );
}
